import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Printable QR poster for the Tenant Reporter Inbox.
 * URL: /admin/qr-poster?slug=chew-flats&title=Chew%20Flats
 * Renders 8.5×11 print-optimized layout. User taps browser Print → Save as PDF.
 */
export default function AdminQrPoster() {
  const [params] = useSearchParams();
  const slug = params.get("slug") || "chew-flats";
  const title = params.get("title") || "Chew Flats";
  const address = params.get("address") || "5346 Chew Ave · LaSalle Pilot";
  const [origin, setOrigin] = useState("");
  useEffect(() => { setOrigin(window.location.origin); }, []);
  const link = `${origin}/report/${slug}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=800x800&margin=0&data=${encodeURIComponent(link)}`;

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; }
          .poster { page-break-after: avoid; }
        }
        @page { size: 8.5in 11in; margin: 0.4in; }
      `}</style>
      <div className="min-h-screen bg-slate-100 p-4 flex flex-col items-center gap-4">
        <div className="no-print flex gap-2 w-full max-w-[8in]">
          <button onClick={() => window.print()} data-testid="qr-print-btn"
            className="h-10 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-xs tracking-widest rounded shadow">
            🖨 Print / Save as PDF
          </button>
          <div className="ml-auto text-xs text-slate-600 self-center">
            Public link: <a href={link} className="text-blue-700 underline" target="_blank" rel="noreferrer">{link}</a>
          </div>
        </div>

        <div className="poster bg-white w-[8in] min-h-[10.5in] p-8 shadow-xl border border-slate-300 flex flex-col text-slate-900">
          {/* Header */}
          <div className="border-b-4 border-[#0f172a] pb-3">
            <div className="text-[11px] font-black uppercase tracking-[0.3em] text-slate-500">Maintenance & Repair · {address}</div>
            <h1 className="mt-1 text-5xl font-black tracking-tight">Report a Problem.<br/>Get it Fixed.</h1>
          </div>

          {/* Big QR + steps */}
          <div className="mt-6 flex-1 grid grid-cols-5 gap-6">
            <div className="col-span-3 flex flex-col items-center justify-center">
              <img src={qrUrl} alt="QR" className="w-[3.6in] h-[3.6in] border-2 border-[#0f172a]"/>
              <div className="mt-3 text-center">
                <div className="font-black text-lg">Scan with your phone camera</div>
                <div className="text-xs text-slate-500 mt-1 font-mono">{link}</div>
              </div>
            </div>
            <ol className="col-span-2 space-y-4 text-[13px] leading-tight">
              <li className="flex gap-2">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-500 text-white font-black flex items-center justify-center">1</span>
                <div><b>Scan the QR</b> — opens a form on your phone. No app to install, no login needed.</div>
              </li>
              <li className="flex gap-2">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-500 text-white font-black flex items-center justify-center">2</span>
                <div><b>Describe & photograph</b> what's broken. Your unit is auto-detected.</div>
              </li>
              <li className="flex gap-2">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-500 text-white font-black flex items-center justify-center">3</span>
                <div><b>Chat with our AI</b> — it might solve it in seconds (clogged drain, tripped breaker, reset button).</div>
              </li>
              <li className="flex gap-2">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-500 text-white font-black flex items-center justify-center">4</span>
                <div><b>Track your ticket</b> — get a private status link. Live updates when a tech is on the way.</div>
              </li>
              <li className="flex gap-2">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-orange-500 text-white font-black flex items-center justify-center">!</span>
                <div><b>Emergency?</b> Water leak, no heat, no power → still submit, then call the number on your lease.</div>
              </li>
            </ol>
          </div>

          {/* Footer */}
          <div className="mt-6 pt-4 border-t-2 border-slate-200 flex items-center justify-between text-[10px]">
            <div>
              <div className="font-black text-slate-800 text-sm">Property Management Redefined</div>
              <div className="text-slate-500">Powered by Field Radar · Live 24/7</div>
            </div>
            <div className="text-right text-slate-400 font-mono">/{slug}</div>
          </div>
        </div>
      </div>
    </>
  );
}
